import {
    Box,
    Button,
    Divider,
    Grid,
    MenuItem,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import React, { ChangeEvent, Component } from 'react';

import { EXERCISE_BOOTY_BAND_OPTIONS, EXERCISE_LEVEL_OPTIONS, EXERCISE_MAT_OPTIONS, EXERCISE_PLAN_OPTIONS, EXERCISE_WEIGHT_OPTIONS } from './exercise.constants';
import { ExerciseFormErrors, ExerciseFormValues } from './exercise.types';
import { createOptionValuesWithCurrentValue } from './exercise.utils';
import ExerciseVideoPreview from './ExerciseVideoPreview';
import ExerciseVideoUploadDropzone from './ExerciseVideoUploadDropzone';

interface Props {
    currentIndex?: number;
    currentTag?: string;
    errors: ExerciseFormErrors;
    savingItem: boolean;
    showDeleteButton?: boolean;
    submitLabel: string;
    title: string;
    values: ExerciseFormValues;
    onCancel: () => void;
    onDelete?: () => void;
    onFieldChanged: (name: keyof ExerciseFormValues, value: string) => void;
    onSubmit: () => void;
}

class ExerciseForm extends Component<Props> {
    onCancelClick = () => {
        this.props.onCancel();
    };

    onDeleteClick = () => {
        this.props.onDelete?.();
    };

    onFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;

        this.props.onFieldChanged(name as keyof ExerciseFormValues, value);
    };

    onSubmitClick = () => {
        this.props.onSubmit();
    };

    onVideoUploaded = (videoUrl: string) => {
        this.props.onFieldChanged('videoUrl', videoUrl);
    };

    render() {
        const { currentIndex, currentTag, errors, savingItem, showDeleteButton, submitLabel, title, values } = this.props;
        const planOptions = createOptionValuesWithCurrentValue(EXERCISE_PLAN_OPTIONS, values.plan);
        const weightOptions = createOptionValuesWithCurrentValue(EXERCISE_WEIGHT_OPTIONS, values.weight);
        const matOptions = createOptionValuesWithCurrentValue(EXERCISE_MAT_OPTIONS, values.mat);
        const bootyBandOptions = createOptionValuesWithCurrentValue(EXERCISE_BOOTY_BAND_OPTIONS, values.bootyBand);

        return (
            <Box>
                <Box sx={{ alignItems: { md: 'center', xs: 'flex-start' }, display: 'flex', flexDirection: { md: 'row', xs: 'column' }, justifyContent: 'space-between', mb: 2.5 }}>
                    <Box>
                        <Button onClick={this.onCancelClick} size="small" sx={{ fontSize: '0.82rem', mb: 0.75, px: 0 }} variant="text">Back to Exercises</Button>
                        <Typography sx={{ fontSize: { md: '1.45rem', xs: '1.3rem' }, fontWeight: 700 }} variant="h4">{title}</Typography>
                        {(currentTag || currentIndex !== undefined) && (
                            <Typography color="text.secondary" sx={{ fontSize: '0.82rem', mt: 0.75 }} variant="body2">
                                {currentTag ? `Current Tag: ${currentTag}` : ''}
                                {currentTag && currentIndex !== undefined ? '  |  ' : ''}
                                {currentIndex !== undefined ? `Index: ${currentIndex}` : ''}
                            </Typography>
                        )}
                    </Box>
                    {showDeleteButton && (
                        <Button color="error" onClick={this.onDeleteClick} size="small" sx={{ fontSize: '0.82rem' }} variant="outlined">Delete Exercise</Button>
                    )}
                </Box>
                <Divider sx={{ mb: 2.5 }} />
                <Paper sx={{ borderRadius: 3, overflow: 'hidden' }} variant="outlined">
                    <Box sx={{ display: 'grid', gap: 3, p: { xs: 1.75, md: 2.5 } }}>
                        <Box>
                            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, mb: 1.25 }} variant="h6">1. Identity</Typography>
                            <Grid container spacing={2}>
                                <Grid item md={6} xs={12}>
                                    <TextField error={Boolean(errors.exercise)} fullWidth helperText={errors.exercise} label="Exercise Name *" name="exercise" onChange={this.onFieldChange} size="small" value={values.exercise} />
                                </Grid>
                                <Grid item md={6} xs={12}>
                                    <TextField error={Boolean(errors.tag)} fullWidth helperText={errors.tag || 'Must be unique'} label="Tag *" name="tag" onChange={this.onFieldChange} size="small" value={values.tag} />
                                </Grid>
                                <Grid item md={6} xs={12}>
                                    <TextField error={Boolean(errors.level)} fullWidth helperText={errors.level} label="Level *" name="level" onChange={this.onFieldChange} select size="small" value={values.level}>
                                        <MenuItem value="">Select a level</MenuItem>
                                        {EXERCISE_LEVEL_OPTIONS.map((level) => (
                                            <MenuItem key={level} value={level}>{level}</MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item md={6} xs={12}>
                                    <TextField error={Boolean(errors.plan)} fullWidth helperText={errors.plan || 'Choose the primary training plan grouping.'} label="Plan *" name="plan" onChange={this.onFieldChange} select size="small" value={values.plan}>
                                        <MenuItem value="">Select a plan</MenuItem>
                                        {planOptions.map((option) => (
                                            <MenuItem key={option} value={option}>{option}</MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                            </Grid>
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, mb: 1.25 }} variant="h6">2. Coaching Content</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField error={Boolean(errors.description)} fullWidth helperText={errors.description} label="Description *" minRows={4} multiline name="description" onChange={this.onFieldChange} size="small" value={values.description} />
                                </Grid>
                                <Grid item md={6} xs={12}>
                                    <TextField error={Boolean(errors.step)} fullWidth helperText={errors.step} label="Step *" name="step" onChange={this.onFieldChange} size="small" value={values.step} />
                                </Grid>
                                <Grid item md={6} xs={12}>
                                    <TextField error={Boolean(errors.reps)} fullWidth helperText={errors.reps} label="Reps *" name="reps" onChange={this.onFieldChange} size="small" value={values.reps} />
                                </Grid>
                            </Grid>
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, mb: 1.25 }} variant="h6">3. Equipment & Difficulty</Typography>
                            <Grid container spacing={2}>
                                <Grid item md={6} xs={12}>
                                    <TextField error={Boolean(errors.weight)} fullWidth helperText={errors.weight || 'Use the nearest internal weight label.'} label="Weight *" name="weight" onChange={this.onFieldChange} select size="small" value={values.weight}>
                                        <MenuItem value="">Select a weight</MenuItem>
                                        {weightOptions.map((option) => (
                                            <MenuItem key={option} value={option}>{option}</MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item md={6} xs={12}>
                                    <TextField error={Boolean(errors.weightRange)} fullWidth helperText={errors.weightRange} label="Weight Range *" name="weightRange" onChange={this.onFieldChange} size="small" value={values.weightRange} />
                                </Grid>
                                <Grid item md={6} xs={12}>
                                    <TextField error={Boolean(errors.mat)} fullWidth helperText={errors.mat} label="Mat Required *" name="mat" onChange={this.onFieldChange} select size="small" value={values.mat}>
                                        <MenuItem value="">Select an option</MenuItem>
                                        {matOptions.map((option) => (
                                            <MenuItem key={option} value={option}>{option}</MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item md={6} xs={12}>
                                    <TextField error={Boolean(errors.bootyBand)} fullWidth helperText={errors.bootyBand || 'Choose the band requirement that appears in workouts.'} label="Booty Band *" name="bootyBand" onChange={this.onFieldChange} select size="small" value={values.bootyBand}>
                                        <MenuItem value="">Select an option</MenuItem>
                                        {bootyBandOptions.map((option) => (
                                            <MenuItem key={option} value={option}>{option}</MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                            </Grid>
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, mb: 1.25 }} variant="h6">4. Media</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField error={Boolean(errors.videoUrl)} fullWidth helperText={errors.videoUrl} label="High-Res Video URL *" name="videoUrl" onChange={this.onFieldChange} size="small" value={values.videoUrl} />
                                </Grid>
                                <Grid item xs={12}>
                                    <ExerciseVideoUploadDropzone
                                        disabled={savingItem}
                                        onVideoUploaded={this.onVideoUploaded}
                                        videoUrl={values.videoUrl}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <ExerciseVideoPreview title={values.exercise || 'this exercise'} videoUrl={values.videoUrl} />
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>
                    <Divider />
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', p: 2.25 }}>
                        <Button onClick={this.onCancelClick} size="small" sx={{ fontSize: '0.82rem' }} variant="text">Cancel</Button>
                        <Button disabled={savingItem} onClick={this.onSubmitClick} size="small" sx={{ fontSize: '0.82rem' }} variant="contained">{submitLabel}</Button>
                    </Box>
                </Paper>
            </Box>
        );
    }
}

export default ExerciseForm;